import React, { useEffect, useRef, useState } from 'react'
import { throttle } from 'lodash-es'
import uniqueIds from '@/config/unique-ids.json'
import { ProjectConfig, getUniqueId } from '@/project-config/index'
import lessModule from './index.module.less'

const uniqueId = getUniqueId()
const { bgMap, imgMap, textImgList, textImgWidth } = ProjectConfig.loginRight

const { bgColor, bg1, bgu } = bgMap
const { rightPlanet, rightLogo, rightText2Img, singleToDouble, doubleTosingle, singleQuoteImg, doubleQuoteImg, rightTextImg } = imgMap

const preloadList = [rightPlanet, rightLogo, rightText2Img, singleToDouble, doubleTosingle, singleQuoteImg, doubleQuoteImg]

export default function LoginRight() {
  const [, forceUpdate] = useState({})
  // const [supportWebp, setSupportWebp] = useState(false)
  const [textSrc, setTextSrc] = useState(rightTextImg)
  const [imgOnload, setImgOnload] = useState(false)
  const [transformX, setTransformX] = useState(0)
  const [transformY, setTransformY] = useState(0)

  const isImgAnimating = useRef(false)
  const bigImgNeedAnimateBack = useRef(false)
  const bigImgStatus = useRef<'logo' | 'planet'>('logo')

  const quoteTimer = useRef(0)

  const getQuoteBg = () => {
    if (imgOnload) {
      if (bigImgStatus.current === 'logo') {
        if (!isImgAnimating.current) {
          return doubleQuoteImg
        }
        return doubleTosingle
      }
      if (bigImgStatus.current === 'planet') {
        if (!isImgAnimating.current) {
          return singleQuoteImg
        }
        return singleToDouble
      }
    }
    return doubleQuoteImg
  }

  const getLogoClass = () => {
    if (imgOnload) {
      if (bigImgStatus.current === 'logo') {
        if (!isImgAnimating.current) {
          return 'up'
        }
        return 'turndown'
      }
      if (bigImgStatus.current === 'planet') {
        if (!isImgAnimating.current) {
          return 'down'
        }
        return 'turnup'
      }
    }
    return 'up'
  }

  const getPlanetClass = () => {
    if (imgOnload) {
      if (bigImgStatus.current === 'logo') {
        if (!isImgAnimating.current) {
          return 'down'
        }
        return 'turnup'
      }
      if (bigImgStatus.current === 'planet') {
        if (!isImgAnimating.current) {
          return 'up'
        }
        return 'turndown'
      }
    }
    return 'down'
  }

  const handleMouseEnter = () => {
    console.log('handleMouseEnter', isImgAnimating.current, bigImgStatus.current)
    if (imgOnload) {
      if (!isImgAnimating.current && bigImgStatus.current === 'logo') {
        clearTimeout(quoteTimer.current)
        isImgAnimating.current = true
        setTextSrc(rightText2Img)
        forceUpdate({})
        quoteTimer.current = window.setTimeout(() => {
          isImgAnimating.current = false
          bigImgStatus.current = 'planet'
          forceUpdate({})
          if (bigImgNeedAnimateBack.current) {
            quoteAnimateBack()
          }
        }, 550)
      }
    }
  }

  const handleMouseLeave = () => {
    console.log('handleMouseLeave', isImgAnimating.current, bigImgStatus.current)
    if (imgOnload) {
      if (!isImgAnimating.current && bigImgStatus.current === 'planet') {
        quoteAnimateBack()
      }
      if (isImgAnimating.current && bigImgStatus.current === 'logo') {
        bigImgNeedAnimateBack.current = true
      }
    }
  }

  const quoteAnimateBack = () => {
    clearTimeout(quoteTimer.current)
    setTextSrc(rightTextImg)
    isImgAnimating.current = true
    bigImgNeedAnimateBack.current = false
    forceUpdate({})
    quoteTimer.current = window.setTimeout(() => {
      isImgAnimating.current = false
      bigImgStatus.current = 'logo'
      forceUpdate({})
    }, 350)
  }

  useEffect(() => {
    const preload = (src: string) => new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        resolve(src)
      }
      img.src = src
    })
    Promise.all(preloadList.map((src) => src ? preload(src) : null)).then(() => {
      setImgOnload(true)
      console.log('login right img preload ok')
    }).catch((e) => {
      console.warn(e)
      console.log('login right img preload fail')
    })
  }, [])

  useEffect(() => {
    const bodyWidth = document.body.offsetWidth
    const bodyHeight = document.body.offsetHeight
    const midX = bodyWidth / 2
    const midY = bodyHeight / 2
    const onMouseMove = throttle((e: MouseEvent) => {
      setTransformX(((e.pageX - midX) / midX) * 5)
      setTransformY(((e.pageY - midY) / midX) * 5)
    }, 100)
    document.body.addEventListener('mousemove', onMouseMove)

    // const checkWebp = async () => {
    //   const sup = await checkAllWebpFeature()
    //   console.log('support webp', sup)
    //   setSupportWebp(sup as boolean)
    // }
    // checkWebp()

    return () => {
      clearTimeout(quoteTimer.current)
      document.body.removeEventListener('mousemove', onMouseMove)
    }
  }, [])

  return (
    <div
      className={lessModule.loginRight}
      style={{ backgroundColor: bgColor }}
    >
      <div
        className={lessModule.quoteWrap}
      >
        <div
          className={`${lessModule.quote} ${isImgAnimating.current ? lessModule[bigImgStatus.current] : ''} ${isImgAnimating.current ? lessModule.play : lessModule.paused}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            backgroundImage: `url(${getQuoteBg()})`,
            backgroundSize: isImgAnimating.current ? undefined : '100% 100%',
          }}
        />
      </div>
      <div className={lessModule.rightContent}>
        <div
          className={lessModule.bg1}
          style={{ backgroundImage: `url(${bg1})` }}
        />
        <div
          className={lessModule.bg2}
          style={{ backgroundImage: `url(${bgu})` }}
        />
        <div className={lessModule.imgWrap}>
          {
            textImgList.map((src) => (
              <img
                key={src}
                style={{
                  display: textSrc === src ? 'inline' : 'none',
                  width: textImgWidth,
                }}
                className={`${lessModule.text} ${textSrc === rightText2Img && uniqueId !== uniqueIds.HSMSF_UNIQUE_ID.id ? lessModule.text2 : ''}`}
                src={src}
                alt=""
              />
            ))
          }
          <div
            className={lessModule.img}
            style={{
              transform: `translate3d(${-transformX}px, ${-transformY}px, 0)`,
            }}
          >
            <img
              src={rightLogo}
              className={`${lessModule.bigLogo} ${lessModule[getLogoClass()]}`}
              alt=""
            />
            <img
              src={rightPlanet}
              className={`${lessModule.bigPlanet} ${lessModule[getPlanetClass()]}`}
              alt=""
            />
          </div>
        </div>
      </div>
      <div className={lessModule.fakeImg}>
        {
          preloadList.map((src) => {
            return src ? (
              <img key={src} src={src} alt="" />
            ) : null
          })
        }
      </div>
    </div>
  )
}
